import './styles/App.css';
import './styles/theme.css';
import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import { applyOrgTheme } from './api/themeService';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => error?.response?.status === 401 ? false : failureCount < 1,
    },
  },
});

const Login          = React.lazy(() => import('./pages/Login'));
const Dashboard      = React.lazy(() => import('./pages/Dashboard'));
const Organisations  = React.lazy(() => import('./pages/Organisations'));
const Organisation      = React.lazy(() => import('./pages/Organisation'));
const UnassignedUsers   = React.lazy(() => import('./pages/UnassignedUsers'));
const TalentPool     = React.lazy(() => import('./pages/TalentPool'));
const JobPostings    = React.lazy(() => import('./pages/JobPostings'));
const CreateJob      = React.lazy(() => import('./pages/CreateJob'));
const EditJob        = React.lazy(() => import('./pages/EditJob'));
const JobApplicants  = React.lazy(() => import('./pages/JobApplicants'));
const ManageSponsors  = React.lazy(() => import('./pages/ManageSponsors'));
const SponsorDetail   = React.lazy(() => import('./pages/SponsorDetail'));
const AboutCompany    = React.lazy(() => import('./pages/AboutCompany'));
const Settings        = React.lazy(() => import('./pages/Settings'));
const Support         = React.lazy(() => import('./pages/Support'));
const ForgotPassword  = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword   = React.lazy(() => import('./pages/ResetPassword'));
const NotFound        = React.lazy(() => import('./pages/NotFound'));
const CvAccess        = React.lazy(() => import('./pages/CvAccess'));
const ShortlistReport = React.lazy(() => import('./pages/ShortlistReport'));

const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen bg-white">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-theme-primary mx-auto mb-4"></div>
      <p className="text-gray-600 font-medium text-sm">Loading...</p>
    </div>
  </div>
);

function AppRoutes() {
  const { isAuthenticated, user } = useAuth();

  // Apply org brand colour for tutors/clubAdmins (those with a club.id on their token)
  useEffect(() => {
    if (isAuthenticated === true && user?.club?.id) {
      applyOrgTheme(user.club.id);
    }
  }, [isAuthenticated, user?.club?.id]);

  if (isAuthenticated === null) return <LoadingSpinner />;

  return (
    <>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route
            path="/login"
            element={!isAuthenticated ? <Login /> : <Navigate to="/dashboard" replace />}
          />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/auth/password-reset/:userId/:token" element={<ResetPassword />} />
          <Route path="/dashboard"
            element={<ProtectedRoute><Dashboard /></ProtectedRoute>}
          />
          {/* Super admin only */}
          <Route path="/organisations"
            element={<ProtectedRoute superOnly><Organisations /></ProtectedRoute>}
          />
          <Route path="/organisations/unassigned"
            element={<ProtectedRoute superOnly><UnassignedUsers /></ProtectedRoute>}
          />
          <Route path="/organisations/:orgId"
            element={<ProtectedRoute superOnly><Organisation /></ProtectedRoute>}
          />
          {/* Club admin only — manage their org's sponsors */}
          <Route path="/manage-sponsors"
            element={<ProtectedRoute adminOnly><ManageSponsors /></ProtectedRoute>}
          />
          <Route path="/manage-sponsors/:id"
            element={<ProtectedRoute adminOnly><SponsorDetail /></ProtectedRoute>}
          />
          {/* Sponsor only */}
          <Route path="/about-company"
            element={<ProtectedRoute><AboutCompany /></ProtectedRoute>}
          />
          {/* All authenticated users */}
          <Route path="/talent-pool"
            element={<ProtectedRoute><TalentPool /></ProtectedRoute>}
          />
          <Route path="/job-postings"
            element={<ProtectedRoute><JobPostings /></ProtectedRoute>}
          />
          <Route path="/job-postings/new"
            element={<ProtectedRoute><CreateJob /></ProtectedRoute>}
          />
          <Route path="/job-postings/:id/edit"
            element={<ProtectedRoute><EditJob /></ProtectedRoute>}
          />
          <Route path="/job-postings/:id/applicants"
            element={<ProtectedRoute><JobApplicants /></ProtectedRoute>}
          />
          <Route path="/job-postings/:id"
            element={<ProtectedRoute><JobApplicants /></ProtectedRoute>}
          />
          <Route path="/reports/shortlist"
            element={<ProtectedRoute><ShortlistReport /></ProtectedRoute>}
          />
          <Route path="/settings"
            element={<ProtectedRoute><Settings /></ProtectedRoute>}
          />
          <Route path="/support"
            element={<ProtectedRoute><Support /></ProtectedRoute>}
          />
          <Route path="/"
            element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />}
          />
          <Route path="/cv-access" element={<CvAccess />} />
          <Route path="*"
            element={isAuthenticated ? <NotFound /> : <Navigate to="/login" replace />}
          />
        </Routes>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
